import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import toast from 'react-hot-toast';

const AlertsContext = createContext();

const STORAGE_KEY = 'poolscope_alerts';

export function AlertsProvider({ children }) {
  const [alerts, setAlerts] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(alerts));
    } catch {}
  }, [alerts]);

  const addAlert = useCallback((poolId, poolName, threshold, direction = 'above') => {
    setAlerts(prev => [
      ...prev.filter(a => !(a.poolId === poolId && a.direction === direction)),
      { poolId, poolName, threshold, direction, createdAt: Date.now(), triggered: false },
    ]);
  }, []);

  const removeAlert = useCallback((poolId, direction) => {
    setAlerts(prev => prev.filter(a => !(a.poolId === poolId && a.direction === direction)));
  }, []);

  const getAlerts = useCallback((poolId) => {
    return alerts.filter(a => a.poolId === poolId);
  }, [alerts]);

  const checkAlerts = useCallback((pools) => {
    if (!pools || pools.length === 0) return;

    setAlerts(prev => {
      let changed = false;
      const updated = prev.map(alert => {
        if (alert.triggered) return alert;
        const pool = pools.find(p => p.id === alert.poolId || p.poolId === alert.poolId);
        if (!pool) return alert;

        const shouldTrigger =
          (alert.direction === 'above' && pool.apr >= alert.threshold) ||
          (alert.direction === 'below' && pool.apr <= alert.threshold);

        if (shouldTrigger) {
          changed = true;
          toast(`${alert.poolName}: APR ${alert.direction === 'above' ? 'reached' : 'dropped to'} ${pool.apr.toFixed(2)}% (threshold: ${alert.threshold}%)`, {
            icon: '\u{1F514}',
            style: { fontFamily: "'IBM Plex Mono', monospace", fontSize: 12 },
            duration: 6000,
          });

          if ('Notification' in window && Notification.permission === 'granted') {
            new Notification('PoolScope APR Alert', {
              body: `${alert.poolName}: APR ${alert.direction === 'above' ? 'reached' : 'dropped to'} ${pool.apr.toFixed(2)}%`,
              icon: '/favicon.ico',
            });
          }

          return { ...alert, triggered: true };
        }
        return alert;
      });
      return changed ? updated : prev;
    });
  }, []);

  const requestNotificationPermission = useCallback(async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      await Notification.requestPermission();
    }
  }, []);

  const resetAlert = useCallback((poolId, direction) => {
    setAlerts(prev => prev.map(a =>
      a.poolId === poolId && a.direction === direction
        ? { ...a, triggered: false }
        : a
    ));
  }, []);

  return (
    <AlertsContext.Provider value={{
      alerts, addAlert, removeAlert, getAlerts, checkAlerts,
      requestNotificationPermission, resetAlert,
    }}>
      {children}
    </AlertsContext.Provider>
  );
}

export function useAlerts() {
  const ctx = useContext(AlertsContext);
  if (!ctx) throw new Error('useAlerts must be used within AlertsProvider');
  return ctx;
}
